import React, { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, FileText, Folder, HardDrive, LoaderCircle, RefreshCw, ShieldCheck, Users } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api, getApiErrorMessage } from '../../lib/api'
import { asArray } from '../../lib/collections'
import { useToast } from '../../ui/ToastProvider'

const number = new Intl.NumberFormat('ko-KR')
const unwrap = (response, fallback) => response?.data?.data ?? response?.data ?? fallback

function formatBytes(value) {
  const bytes = Number(value || 0)
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const scaled = bytes / (1024 ** index)
  return `${scaled.toFixed(index === 0 || scaled >= 10 ? 0 : 1)} ${units[index]}`
}

function RiskList({ title, items, value }) {
  return (
    <section className="min-w-0">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">{title}</h3>
      <ol className="divide-y divide-slate-100 border-y border-slate-200">
        {(items || []).slice(0, 5).map((item) => (
          <li key={item.fileId} className="flex items-center gap-3 py-2.5 text-sm">
            <span className="min-w-0 flex-1 truncate text-slate-700">{item.fileName}</span>
            <span className="shrink-0 text-slate-500">{value(item)}</span>
          </li>
        ))}
      </ol>
    </section>
  )
}

export default function OrgLibraryInsightsPanel({ apiRoot, rootOrgId, canManage, isOperator = false }) {
  const { show } = useToast()
  const [data, setData] = useState(null)
  const [policy, setPolicy] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [purgeFailures, setPurgeFailures] = useState(null)
  const [retryingPurgeId, setRetryingPurgeId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get(`${apiRoot}/workspaces/${rootOrgId}/insights`, {
        __silent: true, __suppressErrorPageRedirect: true,
      })
      const next = unwrap(response, null)
      setData(next)
      setPolicy(next?.policy || null)
      if (isOperator) {
        try {
          const failuresResponse = await api.get(`${apiRoot}/maintenance/purge-failures`, {
            params: { rootOrgId, page: 0, size: 20 },
            __silent: true,
            __suppressErrorPageRedirect: true,
          })
          setPurgeFailures(unwrap(failuresResponse, null))
        } catch {
          setPurgeFailures(null)
        }
      }
    } catch (error) {
      show(getApiErrorMessage(error, '자료실 이용 현황을 불러오지 못했습니다.'), 'error')
    } finally {
      setLoading(false)
    }
  }, [apiRoot, isOperator, rootOrgId, show])

  useEffect(() => { load() }, [load])

  const savePolicy = async (event) => {
    event.preventDefault()
    setSaving(true)
    try {
      setPolicy(unwrap(await api.put(`${apiRoot}/workspaces/${rootOrgId}/policy`, policy), policy))
      show('자료실 운영 정책을 저장했습니다.', 'success')
    } catch (error) {
      show(getApiErrorMessage(error, '자료실 운영 정책을 저장하지 못했습니다.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const retryPurge = async (fileId) => {
    setRetryingPurgeId(fileId)
    try {
      await api.post(`${apiRoot}/maintenance/purge-failures/${fileId}/retry`)
      show('\uC601\uAD6C \uC0AD\uC81C\uB97C \uC7AC\uCC98\uB9AC\uD588\uC2B5\uB2C8\uB2E4.', 'success')
      await load()
    } catch (error) {
      show(
        getApiErrorMessage(error, '\uC601\uAD6C \uC0AD\uC81C\uB97C \uC7AC\uCC98\uB9AC\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.'),
        'error',
      )
    } finally {
      setRetryingPurgeId(null)
    }
  }

  if (loading) return <div className="flex min-h-[440px] items-center justify-center"><LoaderCircle className="h-7 w-7 animate-spin text-slate-400" /></div>
  if (!data) return <div className="flex min-h-[420px] items-center justify-center text-sm text-slate-500">이용 현황을 불러올 수 없습니다.</div>

  const kpis = [
    ['파일', number.format(data.overview?.fileCount || 0), FileText, 'text-cyan-700'],
    ['폴더', number.format(data.overview?.folderCount || 0), Folder, 'text-amber-700'],
    ['30일 활성 사용자', number.format(data.overview?.activeUsers30d || 0), Users, 'text-emerald-700'],
    ['저장 용량', `${formatBytes(data.usage?.usedBytes)} / ${formatBytes(data.usage?.quotaBytes)}`, HardDrive, 'text-slate-700'],
  ]
  const trend = (asArray(data?.trend)).map((point) => ({
    ...point, label: String(point.date || '').slice(5).replace('-', '.'),
  }))

  return (
    <div className="space-y-8">
      <section className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map(([label, value, Icon, color]) => (
          <div key={label} className="rounded-md border border-slate-200 p-4">
            <div className="flex items-center justify-between text-sm text-slate-500"><span>{label}</span><Icon className={`h-5 w-5 ${color}`} /></div>
            <p className="mt-3 truncate text-xl font-semibold text-slate-950">{value}</p>
          </div>
        ))}
      </section>

      <section className="border-y border-slate-200 py-5">
        <div className="mb-4 flex flex-wrap justify-between gap-2">
          <div><h3 className="font-semibold text-slate-900">최근 14일 이용 추이</h3><p className="mt-1 text-sm text-slate-500">업로드 {number.format(data.overview?.uploads30d || 0)} · 다운로드 {number.format(data.overview?.downloads30d || 0)}</p></div>
          <p className="text-sm text-slate-500">검토 {number.format(data.overview?.pendingReviews || 0)} · 업로드 요청 {number.format(data.overview?.pendingUploadRequests || 0)}</p>
        </div>
        <div className="h-[290px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ right: 12, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} />
              <Tooltip /><Legend />
              <Line type="monotone" dataKey="uploads" name="업로드" stroke="#0891b2" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="downloads" name="다운로드" stroke="#059669" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="activeUsers" name="활성 사용자" stroke="#d97706" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="grid gap-7 xl:grid-cols-2">
        <div className="h-[300px] min-w-0">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">파일 형식별 저장 용량</h3>
          <ResponsiveContainer width="100%" height="90%">
            <BarChart data={asArray(data?.storageTypes)} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tickFormatter={formatBytes} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="label" width={75} tick={{ fontSize: 12 }} />
              <Tooltip formatter={formatBytes} />
              <Bar dataKey="sizeBytes" name="사용량" fill="#0891b2" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="min-w-0">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">조직원 이용 현황</h3>
          <div className="overflow-x-auto border-y border-slate-200">
            <table className="w-full min-w-[430px] text-sm">
              <thead><tr className="border-b text-xs text-slate-500"><th className="py-2 text-left">이름</th><th className="text-right">활동</th><th className="text-right">업로드</th><th className="text-right">다운로드</th></tr></thead>
              <tbody>{(asArray(data?.memberUsage)).slice(0, 10).map((member) => (
                <tr key={member.userName} className="border-b border-slate-100 last:border-0"><td className="max-w-48 truncate py-2.5 font-medium">{member.userName}</td><td className="text-right">{number.format(member.activityCount)}</td><td className="text-right">{number.format(member.uploadCount)}</td><td className="text-right">{number.format(member.downloadCount)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="grid gap-7 border-y border-slate-200 py-5 xl:grid-cols-3">
        <div><h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-5 w-5 text-emerald-600" />보안 확인</h3><dl className="divide-y border-y"><div className="flex justify-between py-3 text-sm"><dt>안전 검사 대기</dt><dd>{number.format(data.security?.unverifiedFiles || 0)}</dd></div><div className="flex justify-between py-3 text-sm"><dt>민감정보 확인 필요</dt><dd className="text-amber-700">{number.format(data.security?.sensitiveFiles || 0)}</dd></div></dl></div>
        <RiskList title="용량 상위 파일" items={data.largeFiles} value={(file) => formatBytes(file.sizeBytes)} />
        <RiskList title="장기 미사용 파일" items={data.staleFiles} value={(file) => new Date(file.updatedAt).toLocaleDateString('ko-KR')} />
      </section>

      {asArray(data?.downloadAlerts).length > 0 && <section><h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><AlertTriangle className="h-5 w-5 text-amber-600" />다운로드 확인 필요</h3><ol className="divide-y border-y">{asArray(data?.downloadAlerts).map((alert) => <li key={`${alert.userName}:${alert.firstAt}`} className="flex justify-between py-3 text-sm"><span>{alert.userName}</span><strong className="text-amber-700">{number.format(alert.downloadCount)}회</strong></li>)}</ol></section>}

      {isOperator && purgeFailures?.items?.length > 0 && (
        <section className="border-y border-rose-200 py-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
              {'\uC601\uAD6C \uC0AD\uC81C \uC7AC\uCC98\uB9AC'}
            </h3>
            <span className="text-sm font-medium text-rose-700">
              {number.format(purgeFailures.totalItems || 0)}{'\uAC74'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="py-2 pr-3">{'\uD30C\uC77C'}</th>
                  <th className="w-28 py-2">{'\uD06C\uAE30'}</th>
                  <th className="w-20 py-2">{'\uC2DC\uB3C4'}</th>
                  <th className="py-2 pr-3">{'\uC2E4\uD328 \uC0AC\uC720'}</th>
                  <th className="w-24 py-2 text-right">{'\uC791\uC5C5'}</th>
                </tr>
              </thead>
              <tbody>
                {purgeFailures.items.map((failure) => (
                  <tr key={failure.fileId} className="border-b border-slate-100">
                    <td className="max-w-64 truncate py-2.5 pr-3 font-medium text-slate-800">{failure.fileName}</td>
                    <td className="py-2.5 text-slate-500">{formatBytes(failure.sizeBytes)}</td>
                    <td className="py-2.5 text-slate-500">{number.format(failure.attempts || 0)}</td>
                    <td className="max-w-80 truncate py-2.5 pr-3 text-slate-500" title={failure.error || ''}>{failure.error || '-'}</td>
                    <td className="py-2.5 text-right">
                      <button
                        type="button"
                        disabled={retryingPurgeId != null}
                        onClick={() => retryPurge(failure.fileId)}
                        className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-300 px-2.5 font-medium text-slate-700 disabled:opacity-50"
                      >
                        <RefreshCw className={`h-4 w-4 ${retryingPurgeId === failure.fileId ? 'animate-spin' : ''}`} />{'\uC7AC\uC2DC\uB3C4'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {canManage && policy && (
        <form onSubmit={savePolicy} className="border-t border-slate-200 pt-5">
          <h3 className="mb-4 font-semibold text-slate-900">운영 정책</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            {[['trashRetentionDays', '휴지통 보관', 7, 90, '일'], ['versionRetentionDays', '버전 보관', 30, 365, '일'], ['downloadAlertThreshold', '시간당 다운로드 확인', 10, 1000, '회']].map(([key, label, min, max, unit]) => (
              <label key={key} className="text-sm font-medium text-slate-700">{label}<div className="mt-2 flex items-center gap-2"><input type="number" min={min} max={max} value={policy[key]} onChange={(event) => setPolicy((value) => ({ ...value, [key]: Number(event.target.value) }))} className="h-10 min-w-0 flex-1 rounded-md border border-slate-300 px-3" /><span className="text-slate-500">{unit}</span></div></label>
            ))}
          </div>
          <div className="mt-4 flex justify-end"><button type="submit" disabled={saving} className="h-9 rounded-md bg-sgez-primary px-4 text-sm font-semibold text-white disabled:opacity-50">정책 저장</button></div>
        </form>
      )}
    </div>
  )
}
